// libraries
import React, { useState } from 'react';
import { Typography, Dropdown, Menu, Divider } from 'antd';
import styled from 'styled-components/macro';

// constants & types
import { RelatedTermTypes } from '../shared/components/styled/search/types';
import { EntityType } from '../../../types.generated';
import { ANTD_GRAY } from '../shared/constants';

// utils
import utilService from '../../../utils/util.service';

// components
import AddRelatedNodeModal from '../glossaryTerm/profile/AddRelatedTermsModal';
import { BrowseMenu } from '../glossaryTerm/profile/GlossaryRelatedTerms';
import { EmptyTab } from '../shared/components/styled/EmptyTab';
import RelatedNode from '../glossaryTerm/profile/RelatedTerm';

// hooks
import { useTranslation } from '../../../i18n/useTranslation';

// assets
import { ReactComponent as MoreAction } from '../../../images/more-vert.svg';

export enum RelatedNodeTypes {
    typeOfRelations = 'Type Of',
    isTypeOf = 'Has Type',
    hasRelatedTerms = 'Contains',
    isRelatedTerms = 'Inherits',
}

export type Props = {
    loading?: boolean;
    glossaryRelatedNodeType: string;
    glossaryRelatedNodeResult: Array<any>;
    trackTermLoading: (urn: string, trackLoading: boolean) => void;
};

const ListContainer = styled.div`
    width: 100%;
    background-color: ${ANTD_GRAY[1]};
`;

const ActionButton = styled.div`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

const TitleContainer = styled.div`
    align-items: center;
    border-bottom: solid 1px ${ANTD_GRAY[4]};
    display: flex;
    justify-content: space-between;
    padding: 15px 20px;
`;

export default function GlossaryRelatedNodesResult({
    loading,
    trackTermLoading,
    glossaryRelatedNodeType,
    glossaryRelatedNodeResult,
}: Props) {
    const { t } = useTranslation();
    const [isShowingAddModal, setIsShowingAddModal] = useState(false);
    const [isAddingTerm, setIsAddingTerm] = useState(false);
    const glossaryRelatedNodeUrns: Array<string> = [];

    glossaryRelatedNodeResult.forEach((item: any) => {
        glossaryRelatedNodeUrns.push(item?.entity?.urn);
    });

    const handleAddTerm = () => {
        setIsAddingTerm(true);
        setIsShowingAddModal(true);
    };

    const handleAddTermGroup = () => {
        setIsAddingTerm(false);
        setIsShowingAddModal(true);
    };

    const handleCloseModal = () => {
        setIsShowingAddModal(false);
        setIsAddingTerm(false);
    };

    /**
     * Maps the glossary related node type to the corresponding RelatedTermTypes value.
     * Falls back to RelatedTermTypes.isTypeOf if no match is found.
     */
    const getRelationshipType = () => {
        if (glossaryRelatedNodeType === RelatedNodeTypes.typeOfRelations) {
            return RelatedTermTypes.typeOfRelations;
        }
        if (glossaryRelatedNodeType === RelatedNodeTypes.hasRelatedTerms) {
            return RelatedTermTypes.hasRelatedTerms;
        }
        if (glossaryRelatedNodeType === RelatedNodeTypes.isRelatedTerms) {
            return RelatedTermTypes.isRelatedTerms;
        }
        return RelatedTermTypes.isTypeOf;
    };

    // Show the dropdown menu
    const browseMenu = (
        <BrowseMenu>
            <Menu.Item key="add-term" className="f-color-gray-s100" onClick={handleAddTerm}>
                {t('Add Glossary Term')}
            </Menu.Item>
            <Divider className="ma-0" />
            <Menu.Item key="add-group" className="f-color-gray-s100" onClick={handleAddTermGroup}>
                {t('Add Glossary Term Group')}
            </Menu.Item>
        </BrowseMenu>
    );

    return (
        <>
            <ListContainer>
                <TitleContainer>
                    <Typography.Title style={{ margin: '0' }} level={3}>
                        {glossaryRelatedNodeType}
                    </Typography.Title>
                    <Dropdown overlay={browseMenu} trigger={['click']} placement="bottomRight">
                        <ActionButton>
                            <MoreAction
                                width={12}
                                height={12}
                                className="f-color-gray-s100"
                                data-testid="entity-header-dropdown"
                            />
                            <span className="f-color-gray-s100 pl-1">{t('Actions')}</span>
                        </ActionButton>
                    </Dropdown>
                </TitleContainer>
                {glossaryRelatedNodeUrns.map((urn) => (
                    <RelatedNode
                        key={urn}
                        urn={urn}
                        isLoading={loading}
                        relationshipType={getRelationshipType()}
                        entityType={utilService.getEntityTypeFromUrn(urn)}
                        onLoadingChange={(isLoading) => trackTermLoading?.(urn, isLoading)}
                    />
                ))}
                {glossaryRelatedNodeUrns.length === 0 && (
                    <EmptyTab tab={`node ${glossaryRelatedNodeType.toLocaleLowerCase()}`} />
                )}
            </ListContainer>
            {isShowingAddModal && (
                <AddRelatedNodeModal
                    isTypeTermGroup={!isAddingTerm}
                    entityType={isAddingTerm ? EntityType.GlossaryTerm : EntityType.GlossaryNode}
                    isAddingTermFromNode={isAddingTerm}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
}
